import React, { memo } from 'react';
import type { DLClickEventHandlerProps, ProductCardEntryType, ProductProps } from 'tsconfig/types';
import type { ProductCardPriceTypes } from '../../ProductCard/ProductCard';
import { ProductCard } from '../../ProductCard/ProductCard';
import useProductData from '../../../hooks/useProductData';
import get from 'lodash/get';
import { globals, showPricePerLitre } from 'common-ui';
export interface CmsProductCardProps {
  contentItem: ProductCardEntryType;
  dlClickEventHandler?: DLClickEventHandlerProps;
}

function buildMultiViewPrices(products: ProductProps[], isApac: boolean): ProductCardPriceTypes[] {
  return products.map(product => {
    const multiViewVppPrice = get(product, 'skus[0].vppPrice', null);
    const multiViewSalePrice = get(product, 'skus[0].salePrice', null);
    const multiViewBuyersRRP = get(product, 'skus[0].buyersRRP', null);
    const multiViewMemberPrice = get(product, 'skus[0].memberPrice', null);
    const multiViewnumberOfBottles = get(product, 'skus[0].numberOfBottles', null);

    return {
      productPrice: isApac ? multiViewVppPrice || multiViewSalePrice : multiViewSalePrice,
      productPriceOriginal: multiViewBuyersRRP || 0,
      productMemberPrice: multiViewMemberPrice,
      productNumberOfBottles: multiViewnumberOfBottles,
      memberPricePerLitre: get(product, 'skus[0].memberPricePerLitre'),
    };
  });
}

function hasMultiViewPriceDiff(prices: ProductCardPriceTypes[]): boolean {
  return Boolean(prices && prices[0]?.productPrice !== prices[1]?.productPrice);
}

function resolveProductCode(
  productCardVariant: string | undefined,
  productLink: any[] | undefined,
  productCode: string | undefined,
): string {
  if (productCardVariant === 'Large Product Multi View' && productLink && productLink.length > 0) {
    return productLink[0]?.fields?.productCode || '';
  }
  return productCode || '';
}

function resolveProductType(productData: ProductProps | null | undefined): string {
  if (productData?.mixed) {
    return 'Case';
  }
  return productData?.productType ?? '';
}

function resolveTitle(productCardTitle: string | undefined, productData: ProductProps | null | undefined): string {
  return productCardTitle || `${productData?.name ?? ''} ${productData?.vintage ?? ''}`.trim();
}

export const CmsProductCard = memo(({ contentItem, dlClickEventHandler }: CmsProductCardProps) => {
  const {
    productCode,
    productCardBackgroundImage,
    productCardButton,
    productCardVariant,
    productCardBackgroundColor,
    productCardTitle,
    productCardTitleUrl,
    productCardSubTitle,
    productCardDescription,
    productImageOverride,
    productImageSecondary,
    productLink,
    hidePriceOnMultiView,
  } = contentItem.fields || {};
  const { product: singleProductData, loading } = useProductData(productCode);

  let productData = singleProductData;
  const multiViewProducts: ProductProps[] = [];

  const links = productLink || [];
  for (const link of links) {
    const code = link?.fields?.productCode;
    if (!code) {
      continue;
    }
    const { product } = useProductData(code);
    if (product) {
      multiViewProducts.push(product);
    }
  }

  if (productCardVariant === 'Large Product Multi View') {
    [productData] = multiViewProducts;
  }

  const isApac = globals.country === 'au';
  const vppPrice = get(productData, 'skus[0].vppPrice', null);
  const salePrice = get(productData, 'skus[0].salePrice', null);
  const buyersRRP = get(productData, 'skus[0].buyersRRP', null);
  const memberPrice = get(productData, 'skus[0].memberPrice', null);
  const numberOfBottles = get(productData, 'skus[0].numberOfBottles', null);
  const referencedSkus = get(productData, 'skus[0].referencedSkus', null);

  const multiViewPrices = buildMultiViewPrices(multiViewProducts, isApac);
  const multiViewPriceDiff = hasMultiViewPriceDiff(multiViewPrices);
  const pricePerLitreSection = showPricePerLitre(resolveProductType(productData));

  return (
    <ProductCard
      productName={productData?.name || ''}
      productRating={productData?.averageOverallRating}
      productReviewCount={productData?.totalReviewCount}
      productPrice={isApac ? vppPrice || salePrice : salePrice}
      productPriceOriginal={buyersRRP || 0}
      productPricePrefix={isApac && vppPrice ? 'from ' : ''}
      productMemberPrice={memberPrice}
      productImage={productData?.smallImage || productData?.largeImage}
      productVintage={productData?.vintage || ''}
      productCardBackgroundImage={productCardBackgroundImage}
      productCardBackgroundColor={productCardBackgroundColor}
      productCardButton={productCardButton}
      productCode={resolveProductCode(productCardVariant, productLink, productCode)}
      productCardVariant={productCardVariant}
      productType={resolveProductType(productData)}
      productNumberOfBottles={numberOfBottles}
      productCardTitle={resolveTitle(productCardTitle, productData)}
      productCardTitleUrl={productCardTitleUrl}
      productCardSubTitle={productCardSubTitle}
      productDescription={productData?.description}
      productCardDescription={productCardDescription}
      productImageOverride={productImageOverride}
      productImageSecondary={productImageSecondary}
      productLink={productLink}
      multiViewPrices={multiViewPrices}
      multiViewPriceDiff={multiViewPriceDiff}
      hidePriceOnMultiView={hidePriceOnMultiView}
      isLoading={loading}
      product={productData || ({} as ProductProps)}
      referencedSkus={referencedSkus || []}
      dlClickEventHandler={dlClickEventHandler}
      salesPricePerLitre={get(productData, 'skus[0].salePricePerLitre')}
      memberPricePerLitre={get(productData, 'skus[0].memberPricePerLitre')}
      showPricePerLitre={pricePerLitreSection}
    />
  );
});
